import { useCallback, useEffect, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import { button } from 'leva';
import type { Schema } from 'leva/dist/declarations/src/types/public.js';
import type { Sight } from '../../types';
import type { SplatDevHandles } from '../../state/splatDevTools';
import { goToSight, updateSightCameraConfig } from '../../state/appState';
import { computeSphericalFromCamera } from './computeSphericalFromCamera';
import { orbitPoseForSight } from '../../scene/sdk/createSdkScene';

type ChangeContext = { initial?: boolean };

const EDIT_COOLDOWN_MS = 500;

/**
 * "Sight Camera Config" block for the SDK dev panel — a port of the legacy
 * `SightCameraConfigPanel` against engine handles. Live-apply differs in two
 * SDK-specific ways:
 *   1. the fly is `engine.setCameraState(orbitPoseForSight(updated),
 *      {isAnimated})` (legacy flew via sightManager.moveToSight);
 *   2. the sight's SDK `Poi.camera` block is ALSO patched via
 *      `engine.updatePoi` — Poi camera blocks are built once at mount, so
 *      without the patch a 3D marker click would fly to the STALE pose.
 * The rAF mirror + programmatic-set ref + 500 ms edit-cooldown pair is the
 * load-bearing legacy idiom (Leva's set() re-enters onChange synchronously).
 */
export const useSightCameraSync = (
  handles: SplatDevHandles | null,
): { buildSchema: () => Schema; schemaDeps: unknown[]; captureSetGet: (set: unknown, get: unknown) => void } => {
  const engine = handles?.engine ?? null;
  const pushSightCameraConfig = useUnit(updateSightCameraConfig);

  // The last-targeted sight, off `goToSight` — chat chips and 3D marker clicks
  // on both engines, the same source `$currentSight` follows — kept local so it
  // resets with the panel's handles.
  const [currentSight, setCurrentSight] = useState<Sight | null>(null);
  useEffect(() => {
    setCurrentSight(null);
    if (!handles) return;
    const unwatch = goToSight.watch(({ id }) => {
      const sight = handles.sights.find((s) => s.id === id);
      if (sight) setCurrentSight(sight);
    });
    return unwatch;
  }, [handles]);

  const isEditingRef = useRef(false);
  const isProgrammaticSetRef = useRef(false);
  const editTimeoutRef = useRef(0);
  const rafRef = useRef(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setRef = useRef<((values: Record<string, any>) => void) | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getRef = useRef<(path: string) => any>(() => 0);

  const currentSightRef = useRef(currentSight);
  currentSightRef.current = currentSight;
  const engineRef = useRef(engine);
  engineRef.current = engine;

  const handleConfigChange = useCallback(
    (key: string, value: number) => {
      const sight = currentSightRef.current;
      const eng = engineRef.current;
      if (!sight || !eng) return;

      isEditingRef.current = true;
      window.clearTimeout(editTimeoutRef.current);
      editTimeoutRef.current = window.setTimeout(() => {
        isEditingRef.current = false;
      }, EDIT_COOLDOWN_MS);

      pushSightCameraConfig({ sightId: sight.id, cameraConfig: { [key]: value } });

      const updated = { ...sight, cameraConfig: { ...sight.cameraConfig, [key]: value } };
      // Keep the local sight accumulating edits (it's panel-local state).
      setCurrentSight(updated);
      const pose = orbitPoseForSight(updated);
      if (!pose) return;
      eng.setCameraState(
        pose.fov != null
          ? { position: pose.position, lookAt: pose.lookAt, fov: pose.fov }
          : { position: pose.position, lookAt: pose.lookAt },
        { isAnimated: true, animationDuration: 300 },
      );
      // Keep the 3D marker's fly target in sync (Poi.camera is mount-time).
      eng.updatePoi(
        { id: String(sight.id), position: [sight.position.x, sight.position.y, sight.position.z], camera: pose },
        { mode: 'patch' },
      );
    },
    [pushSightCameraConfig],
  );

  const config = currentSight?.cameraConfig;

  const buildSchema = useCallback((): Schema => {
    if (!currentSight) return {};
    const mk = (key: string, value: number, min: number, max: number, step: number, label: string) => ({
      value,
      min,
      max,
      step,
      label,
      onChange: (v: number, _path: string, ctx: ChangeContext) => {
        if (ctx.initial || isProgrammaticSetRef.current) return;
        handleConfigChange(key, v);
      },
    });
    return {
      // Generous ranges — Leva silently clamps set() beyond the slider max.
      distance: mk('distance', config?.distance ?? 0.5, 0.05, 100, 0.01, 'Distance'),
      height: mk('height', config?.height ?? 0, -20, 20, 0.01, 'Height'),
      azimuth: mk('azimuth', config?.azimuth ?? 0, -Math.PI * 2, Math.PI * 2, 0.01, 'Azimuth (rad)'),
      polar: mk('polar', config?.polar ?? Math.PI / 2, 0.01, Math.PI - 0.01, 0.01, 'Polar (rad)'),
      'Copy JSON': button(() => {
        const g = getRef.current;
        const output = {
          distance: g('distance'),
          height: g('height'),
          azimuth: g('azimuth'),
          polar: g('polar'),
        };
        const json = JSON.stringify(output, null, 4);
        navigator.clipboard?.writeText(json);
        const sight = currentSightRef.current;
        console.log(`[SplatDevPanel] Sight "${sight?.name ?? sight?.id ?? '?'}":\n${json}`);
      }),
    };
  }, [currentSight, config, handleConfigChange]);

  // rAF mirror: camera → sliders while the user isn't dragging them.
  useEffect(() => {
    if (!engine || !currentSight) return;
    const tick = () => {
      const sight = currentSightRef.current;
      const eng = engineRef.current;
      if (sight && eng && !isEditingRef.current) {
        const s = eng.getCameraState();
        const spherical = computeSphericalFromCamera(
          { x: s.position[0], y: s.position[1], z: s.position[2] } as never,
          sight.position,
          sight.cameraConfig?.height ?? 0,
        );
        const setFn = setRef.current;
        if (setFn) {
          isProgrammaticSetRef.current = true;
          setFn({
            distance: spherical.distance,
            height: spherical.height,
            azimuth: spherical.azimuth,
            polar: spherical.polar,
          });
          isProgrammaticSetRef.current = false;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [engine, currentSight?.id]);

  const captureSetGet = useCallback((set: unknown, get: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setRef.current = set as any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getRef.current = get as any;
  }, []);

  return { buildSchema, schemaDeps: [buildSchema], captureSetGet };
};
